import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import {
  Box, 
  Typography, 
  Paper, 
  Container, 
  TextField, 
  Button, 
  CircularProgress, 
  Card, 
  CardContent, 
  CardActionArea,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Avatar,
  Divider,
  alpha
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Person as PersonIcon,
  Web as WebIcon,
  DesktopWindowsOutlined as DesktopWindowsOutlinedIcon,
  Lock as LockIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
  DriveFileRenameOutlineOutlined as DriveFileRenameOutlineOutlinedIcon,
  TextSnippetOutlined as SnippetsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  Chat as ChatIcon,
  VolumeOff as VolumeOffIcon,
  VolumeUp as VolumeUpIcon,
  AccountCircle as AccountCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { createApiUrl } from '../utils/apiConfig';
import tokenService from '../utils/tokenService';
import { useSnackbar } from './CentralSnackbar';

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
      style={{ height: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: 3, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Profile = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useSnackbar();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // User data state
  const [currentUser, setCurrentUser] = useState({ 
    username: '', 
    first_name: '', 
    last_name: '', 
    academic_degree: '' 
  });
  
  // Username change state
  const [newUsername, setNewUsername] = useState('');
  const [usernameLoading, setUsernameLoading] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Viewer preference state
  const [viewerPreference, setViewerPreference] = useState('web');
  const [viewerConfig, setViewerConfig] = useState({
    web: { base_url: '', test_study_uid: '' },
    client: { base_url: '', test_study_uid: '' }
  });
  const [viewerLoading, setViewerLoading] = useState(false);
  
  // Chat settings state
  const [chatSettings, setChatSettings] = useState({
    muted: false,
    notificationSound: 'Bright'
  });
  const [chatLoading, setChatLoading] = useState(false);
  
  // Unterschrift State
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState('');
  
  // Snippets State
  const [snippets, setSnippets] = useState([]);
  const [adminSnippets, setAdminSnippets] = useState([]);
  const [userSnippets, setUserSnippets] = useState([]);
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const [snippetDialogOpen, setSnippetDialogOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState(null);
  const [deleteSnippetDialogOpen, setDeleteSnippetDialogOpen] = useState(false);
  const [snippetToDelete, setSnippetToDelete] = useState(null);
  const [snippetFormData, setSnippetFormData] = useState({
    label: '',
    name: '',
    content: ''
  });

  // AI Settings State
  const [aiPrompts, setAiPrompts] = useState([]);
  const [aiPromptsLoading, setAiPromptsLoading] = useState(false);
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const [editingAiPrompt, setEditingAiPrompt] = useState(null);
  const [aiPromptFormData, setAiPromptFormData] = useState({ name: '', prompt: '', show_in_search: false, scope: 'full_history' });
  const [deleteAiPromptDialogOpen, setDeleteAiPromptDialogOpen] = useState(false);
  const [aiPromptToDelete, setAiPromptToDelete] = useState(null);

  // Load user data and viewer config on component mount
  useEffect(() => {
    loadViewerData();
    loadAiSettings();
  }, []);

  // Load snippets after currentUser is available
  useEffect(() => {
    if (currentUser?.id) {
      loadSnippets();
    }
  }, [currentUser?.id]);

  const loadViewerData = async () => {
    try {
      const token = tokenService.getToken();
      
      // Load current user data
      const userResponse = await fetch(createApiUrl('auth/me'), {
        headers: { ...getAuthHeaders() }
      });
      const userData = await userResponse.json();
      
      if (userData.username) {
        setCurrentUser({
          id: userData.id,
          username: userData.username,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          academic_degree: userData.academic_degree || ''
        });
      }
      
      // Unterschrift laden (mit Cache-Buster)
      if (userData.id) {
        try {
          const timestamp = new Date().getTime();
          const sigRes = await fetch(createApiUrl(`profile/signature/${userData.id}?t=${timestamp}`), {
            headers: { ...getAuthHeaders() }
          });
          if (sigRes.ok) {
            const blob = await sigRes.blob();
            setSignaturePreview(URL.createObjectURL(blob));
          } else {
            setSignaturePreview('');
          }
        } catch (e) {
          setSignaturePreview('');
        }
      }
      
      // Load viewer config
      const configResponse = await fetch(createApiUrl('profile/viewer-config'), {
        headers: { ...getAuthHeaders() }
      });
      const configData = await configResponse.json();
      
      if (configData.success) {
        setViewerConfig(configData.viewer_config);
      }
      
      // Load user's viewer preference
      const prefResponse = await fetch(createApiUrl('profile/viewer-preference'), {
        headers: { ...getAuthHeaders() }
      });
      const prefData = await prefResponse.json();
      
      if (prefData.viewer_preference) {
        setViewerPreference(prefData.viewer_preference);
      }
      
      // Load chat settings
      if (userData.chat_settings) {
        setChatSettings({
          muted: userData.chat_settings.muted || false,
          notificationSound: userData.chat_settings.notificationSound || 'Bright'
        });
      }
    } catch (error) {
      console.error('Error loading viewer data:', error);
    }
  };

  const handleUsernameChange = async () => {
    if (!newUsername) {
      showError(t('profile.usernameChange.newUsernameRequired', 'Neuer Benutzername ist erforderlich'), 'ERR-PROFILE-022');
      return;
    }

    if (newUsername === currentUser.username) {
      showError(t('profile.usernameChange.sameUsername', 'Der neue Benutzername ist identisch mit dem aktuellen'), 'ERR-PROFILE-023');
      return;
    }

    setUsernameLoading(true);

    try {
      const response = await fetch(createApiUrl('profile/change-username'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ 
          newUsername: newUsername
        })
      });

      const data = await response.json();
      
      if (data.success) {
        showSuccess(data.message || t('profile.usernameChange.changeSuccess', 'Benutzername erfolgreich geändert'));
        setCurrentUser({ ...currentUser, username: data.new_username });
        setNewUsername('');
      } else {
        showError(data.message || t('profile.usernameChange.changeError', 'Fehler beim Ändern des Benutzernamens'), 'ERR-PROFILE-024');
      }
    } catch (error) {
      showError(t('profile.usernameChange.changeError', 'Fehler beim Ändern des Benutzernamens'), 'ERR-PROFILE-025');
    } finally {
      setUsernameLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showError(t('login.forcePasswordChangeDialog.errorMismatch'), 'ERR-PROFILE-001');
      return;
    }

    if (!passwordData.new_password) {
      showError(t('profile.password.newPasswordRequired'), 'ERR-PROFILE-002');
      return;
    }

    setPasswordLoading(true);

    try {
      const token = tokenService.getToken();
      const response = await fetch(createApiUrl('profile/change-password'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ 
          currentPassword: passwordData.current_password,
          newPassword: passwordData.new_password 
        })
      });

      const data = await response.json();
      
      if (data.success) {
        showSuccess(data.message || t('profile.password.changeSuccess'));
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        showError(data.message || t('profile.password.changeError'), 'ERR-PROFILE-003');
      }
    } catch (error) {
      showError(t('profile.password.changeError'), 'ERR-PROFILE-004');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleViewerPreferenceChange = async (newPreference) => {
    if (newPreference === viewerPreference) return;

    setViewerLoading(true);

    try {
      const token = tokenService.getToken();
      const response = await fetch(createApiUrl('profile/viewer-preference'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ viewer_preference: newPreference })
      });

      const data = await response.json();
      if (data.success) {
        setViewerPreference(newPreference);
        showSuccess(data.message || t('profile.viewer.updateSuccess'));
      } else {
        showError(data.message || t('profile.viewer.updateError'), 'ERR-PROFILE-005');
      }
    } catch (error) {
      showError(t('profile.viewer.updateError'), 'ERR-PROFILE-006');
    } finally {
      setViewerLoading(false);
    }
  };

  const handleChatSettingsChange = async (newSettings) => {
    setChatLoading(true);

    try {
      const response = await fetch(createApiUrl('profile/chat-settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ chat_settings: newSettings })
      });

      const data = await response.json();
      if (data.success) {
        setChatSettings(newSettings);
        showSuccess(data.message || t('profile.chat.updateSuccess'));
      } else {
        showError(data.message || t('profile.chat.updateError'), 'ERR-PROFILE-019');
      }
    } catch (error) {
      showError(t('profile.chat.updateError'), 'ERR-PROFILE-020');
    } finally {
      setChatLoading(false);
    }
  };

  const handleTestSound = () => {
    if (chatSettings.notificationSound) {
      const audio = new Audio(`${process.env.PUBLIC_URL}/statics/Sounds/${chatSettings.notificationSound}.mp3`);
      audio.play().catch(err => {
        console.error('Error playing sound:', err);
        showError('Fehler beim Abspielen des Sounds', 'ERR-PROFILE-021');
      });
    }
  };

  // Unterschrift Upload Handler
  const handleSignatureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'image/png') {
      showError(t('profile.signature.onlyPng'), 'ERR-PROFILE-007');
      return;
    }
    if (file.size > 1024 * 1024) { // 1MB Limit
      showError(t('profile.signature.tooLarge'), 'ERR-PROFILE-008');
      return;
    }
    setSignatureFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setSignaturePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Datei an Backend hochladen
    try {
      const token = tokenService.getToken();
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(createApiUrl('profile/upload-signature'), {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
        },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        showSuccess(t('profile.signature.uploadSuccess'));
        
        // Unterschrift vom Backend neu laden (mit Cache-Buster)
        if (currentUser?.id) {
          try {
            const timestamp = new Date().getTime();
            const sigRes = await fetch(createApiUrl(`profile/signature/${currentUser.id}?t=${timestamp}`), {
              headers: { ...getAuthHeaders() }
            });
            if (sigRes.ok) {
              const blob = await sigRes.blob();
              setSignaturePreview(URL.createObjectURL(blob));
            }
          } catch (e) {
            console.error('Fehler beim Neuladen der Unterschrift:', e);
          }
        }
      } else {
        showError(data.detail || t('profile.signature.uploadError'), 'ERR-PROFILE-009');
      }
    } catch (err) {
      showError(t('profile.signature.uploadError'), 'ERR-PROFILE-010');
    }
  };

  // Snippets functions
  const loadSnippets = async () => {
    setSnippetsLoading(true);
    try {
      // Only load snippets if we have user data
      if (!currentUser?.id) {
        setSnippetsLoading(false);
        return;
      }

      // Load user-accessible snippets (admin and group snippets)
      const response = await fetch(createApiUrl('snippets/for-user'), {
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        
        // Snippets mit allen permissions NULL sind immer "verfügbar" (für alle)
        // Nur Snippets mit spezifischen permissions UND user_id === currentUser.id sind "eigene"
        const adminCreated = data.filter(snippet => {
          // Für alle verfügbar (alle permissions NULL)
          const isForAll = !snippet.valid_for_users && !snippet.valid_for_group && !snippet.valid_for_groups;
          if (isForAll) return true; // Immer in "Verfügbare" anzeigen
          
          // Spezifische permissions und nicht eigenes Snippet
          return snippet.user_id !== currentUser?.id;
        });
        
        const userCreated = data.filter(snippet => {
          // Nur eigene Snippets mit spezifischen permissions
          const isForAll = !snippet.valid_for_users && !snippet.valid_for_group && !snippet.valid_for_groups;
          return !isForAll && snippet.user_id === currentUser?.id;
        });
        
        setAdminSnippets(adminCreated);
        setUserSnippets(userCreated);
      } else {
        console.error('Snippets API error:', response.status, response.statusText);
        showError(t('profile.snippets.loadError'), 'ERR-PROFILE-011');
      }
    } catch (err) {
      console.error('Snippets load error:', err);
      showError(t('profile.snippets.loadError'), 'ERR-PROFILE-012');
    } finally {
      setSnippetsLoading(false);
    }
  };

  const handleAddSnippet = () => {
    setEditingSnippet(null);
    setSnippetFormData({
      label: '',
      name: '',
      content: ''
    });
    setSnippetDialogOpen(true);
  };

  const handleEditSnippet = (snippet) => {
    setEditingSnippet(snippet);
    setSnippetFormData({
      label: snippet.label,
      name: snippet.name,
      content: snippet.content
    });
    setSnippetDialogOpen(true);
  };

  const handleSaveSnippet = async () => {
    if (!snippetFormData.label || !snippetFormData.name || !snippetFormData.content) {
      showError(t('profile.snippets.requiredFields'), 'ERR-PROFILE-013');
      return;
    }

    if (!snippetFormData.label.match(/^[A-ZÄÖÜ0-9-]+$/)) {
      showError(t('profile.snippets.invalidLabel'), 'ERR-PROFILE-014');
      return;
    }

    try {
      const url = editingSnippet ? `/snippets/${editingSnippet.id}` : '/snippets/';
      const method = editingSnippet ? 'PUT' : 'POST';
      
      const payload = {
        ...snippetFormData,
        user_id: currentUser?.id, // User's own snippets  
        valid_for_users: currentUser?.id.toString(), // Only for this specific user
        valid_for_group: null,
        valid_for_groups: null
      };
      
      const response = await fetch(createApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showSuccess(editingSnippet 
          ? t('profile.snippets.updateSuccess')
          : t('profile.snippets.createSuccess')
        );
        setSnippetDialogOpen(false);
        loadSnippets();
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('profile.snippets.saveError'), 'ERR-PROFILE-015');
      }
    } catch (err) {
      showError(t('profile.snippets.saveError'), 'ERR-PROFILE-016');
    }
  };

  const handleDeleteSnippet = (snippet) => {
    setSnippetToDelete(snippet);
    setDeleteSnippetDialogOpen(true);
  };

  const confirmDeleteSnippet = async () => {
    try {
      const response = await fetch(createApiUrl(`snippets/${snippetToDelete.id}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showSuccess(t('profile.snippets.deleteSuccess'));
        setDeleteSnippetDialogOpen(false);
        setSnippetToDelete(null);
        loadSnippets();
      } else {
        showError(t('profile.snippets.deleteError'), 'ERR-PROFILE-017');
      }
    } catch (err) {
      showError(t('profile.snippets.deleteError'), 'ERR-PROFILE-018');
    }
  };


  // Hilfsfunktion: Vollständigen Namen zusammenstellen
  const getFullName = (user) => {
    if (!user) return '';
    const parts = [];
    if (user.academic_degree) parts.push(user.academic_degree);
    if (user.first_name) parts.push(user.first_name);
    if (user.last_name) parts.push(user.last_name);
    return parts.length > 0 ? parts.join(' ') : '';
  };

  // Get initials for avatar
  const getInitials = (user) => {
    if (!user) return '';
    const first = user.first_name ? user.first_name[0] : '';
    const last = user.last_name ? user.last_name[0] : '';
    return (first + last).toUpperCase() || user.username?.[0]?.toUpperCase() || '?';
  };

  const fullName = getFullName(currentUser);

  // --- AI Settings CRUD ---
  const loadAiSettings = async () => {
    try {
      const response = await fetch(createApiUrl('profile/ai-settings'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAiPrompts(data.ai_settings?.prompts || []);
      }
    } catch (e) {
      console.error('Error loading AI settings:', e);
    }
  };

  const saveAiSettings = async (prompts) => {
    try {
      const response = await fetch(createApiUrl('profile/ai-settings'), {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ai_settings: { prompts } })
      });
      const data = await response.json();
      if (data.success) {
        showSuccess(t('profile.aiSettings.saveSuccess'));
      } else {
        showError(t('profile.aiSettings.saveError'));
      }
    } catch (e) {
      showError(t('profile.aiSettings.saveError'));
    }
  };

  const handleOpenAiPromptDialog = (prompt = null) => {
    if (prompt) {
      setEditingAiPrompt(prompt);
      setAiPromptFormData({ name: prompt.name, prompt: prompt.prompt, show_in_search: prompt.show_in_search || false, scope: prompt.scope || 'full_history' });
    } else {
      setEditingAiPrompt(null);
      setAiPromptFormData({ name: '', prompt: '', show_in_search: false, scope: 'full_history' });
    }
    setAiPromptDialogOpen(true);
  };

  const handleSaveAiPrompt = () => {
    if (!aiPromptFormData.name.trim() || !aiPromptFormData.prompt.trim()) return;
    let updated;
    if (editingAiPrompt) {
      updated = aiPrompts.map(p =>
        p.id === editingAiPrompt.id
          ? { ...p, name: aiPromptFormData.name.trim(), prompt: aiPromptFormData.prompt.trim(), show_in_search: aiPromptFormData.show_in_search, scope: aiPromptFormData.scope }
          : p
      );
    } else {
      const newPrompt = {
        id: crypto.randomUUID(),
        name: aiPromptFormData.name.trim(),
        prompt: aiPromptFormData.prompt.trim(),
        is_default: aiPrompts.length === 0,
        show_in_search: aiPromptFormData.show_in_search,
        scope: aiPromptFormData.scope
      };
      updated = [...aiPrompts, newPrompt];
    }
    setAiPrompts(updated);
    saveAiSettings(updated);
    setAiPromptDialogOpen(false);
  };

  const handleDeleteAiPrompt = (prompt) => {
    setAiPromptToDelete(prompt);
    setDeleteAiPromptDialogOpen(true);
  };

  const confirmDeleteAiPrompt = () => {
    if (!aiPromptToDelete) return;
    const updated = aiPrompts.filter(p => p.id !== aiPromptToDelete.id);
    if (aiPromptToDelete.is_default && updated.length > 0) {
      updated[0].is_default = true;
    }
    setAiPrompts(updated);
    saveAiSettings(updated);
    setDeleteAiPromptDialogOpen(false);
    setAiPromptToDelete(null);
  };

  const handleToggleDefaultPrompt = (promptId) => {
    const updated = aiPrompts.map(p => ({
      ...p,
      is_default: p.id === promptId
    }));
    setAiPrompts(updated);
    saveAiSettings(updated);
  };

  // Tab configuration
  const tabs = [
    { icon: <PersonIcon />, label: t('profile.usernameChange.title', 'Benutzername') },
    { icon: <LockIcon />, label: t('profile.password.title', 'Passwort') },
    { icon: <SettingsIcon />, label: t('profile.viewer.title', 'Viewer') },
    { icon: <ChatIcon />, label: t('profile.chat.title', 'Chat') },
    { icon: <AutoAwesomeIcon />, label: t('profile.aiSettings.title', 'KI Einstellungen') },
    { icon: <DriveFileRenameOutlineOutlinedIcon />, label: t('profile.signature.title', 'Unterschrift') },
    { icon: <SnippetsIcon />, label: t('profile.snippets.title', 'Snippets') },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', height: 'calc(100vh - 140px)' }}>
      {/* Profile Header */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3,
          background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: 'primary.main',
              fontSize: '1.75rem',
              fontWeight: 600,
              boxShadow: 3
            }}
          >
            {getInitials(currentUser)}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              {fullName || t('navigation.profile')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountCircleIcon fontSize="small" />
              @{currentUser.username}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Paper 
        sx={{ 
          display: 'flex',
          height: 'calc(100% - 160px)',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        {/* Vertical Tabs */}
        <Box 
          sx={{ 
            width: 220,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.5)
          }}
        >
          <Tabs
            orientation="vertical"
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                minHeight: 56,
                justifyContent: 'flex-start',
                textAlign: 'left',
                px: 2.5,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                '&.Mui-selected': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  fontWeight: 600,
                }
              },
              '& .MuiTabs-indicator': {
                left: 0,
                right: 'auto',
                width: 4,
                borderRadius: '0 4px 4px 0'
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                sx={{ gap: 1.5 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Username Tab */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              {t('profile.usernameChange.title', 'Benutzername ändern')}
            </Typography>
            
            {currentUser.username === 'visusadmin' ? (
              <Alert severity="warning" sx={{ maxWidth: 500 }}>
                {t('profile.usernameChange.visusadminProtected', 'Der visusadmin-Benutzername kann nicht geändert werden.')}
              </Alert>
            ) : (
              <Card sx={{ maxWidth: 500, p: 2 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('profile.usernameChange.currentUsername', 'Aktueller Benutzername')}: <strong>{currentUser.username}</strong>
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label={t('profile.usernameChange.newUsername', 'Neuer Benutzername')}
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    helperText={t('profile.usernameChange.helperText', 'Mindestens 3 Zeichen, nur Buchstaben, Zahlen, Unterstriche und Bindestriche')}
                    sx={{ mb: 3 }}
                  />
                  
                  <Button
                    variant="contained"
                    onClick={handleUsernameChange}
                    disabled={usernameLoading || !newUsername}
                    className={usernameLoading || !newUsername ? "button-disabled" : "button"}
                    startIcon={usernameLoading ? <CircularProgress size={20} /> : <PersonIcon />}
                  >
                    {usernameLoading ? t('profile.usernameChange.saving', 'Wird gespeichert...') : t('profile.usernameChange.changeButton', 'Benutzername ändern')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabPanel>

          {/* Password Tab */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon color="primary" />
              {t('profile.password.title')}
            </Typography>
            
            {currentUser.username === 'visusadmin' ? (
              <Alert severity="warning" sx={{ maxWidth: 500 }}>
                {t('profile.password.visusadminProtected')}
              </Alert>
            ) : (
              <Card sx={{ maxWidth: 500, p: 2 }}>
                <CardContent>
                  <TextField
                    fullWidth
                    type="password"
                    label={t('profile.password.currentPassword')}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    type="password"
                    label={t('profile.password.newPassword')}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    type="password"
                    label={t('profile.password.confirmPassword')}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    sx={{ mb: 3 }}
                  />
                  
                  <Button
                    variant="contained"
                    onClick={handlePasswordChange}
                    disabled={passwordLoading || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
                    className={passwordLoading || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password ? "button-disabled" : "button"}
                    startIcon={passwordLoading ? <CircularProgress size={20} /> : <LockIcon />}
                  >
                    {passwordLoading ? t('profile.password.saving') : t('profile.password.changeButton')}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabPanel>

          {/* Viewer Tab */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon color="primary" />
              {t('profile.viewer.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('profile.viewer.description')}
            </Typography>
            
            <Grid container spacing={3} sx={{ maxWidth: 600 }}>
              {/* Web Viewer Card */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card 
                  sx={{ 
                    position: 'relative',
                    bgcolor: viewerPreference === 'web' ? 'action.selected' : 'background.paper',
                    border: viewerPreference === 'web' ? 2 : 1,
                    borderColor: viewerPreference === 'web' ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleViewerPreferenceChange('web')}
                    disabled={viewerLoading}
                    sx={{ height: 160 }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <WebIcon 
                        sx={{ 
                          fontSize: 56, 
                          color: 'orange',
                          mb: 1.5
                        }} 
                      />
                      <Typography variant="h6" component="div">
                        {t('profile.viewer.webViewer')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile.viewer.browserBased')}
                      </Typography>
                      {viewerPreference === 'web' && (
                        <CheckIcon 
                          sx={{ 
                            position: 'absolute', 
                            top: 12, 
                            right: 12, 
                            color: 'primary.main' 
                          }} 
                        />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>

              {/* Client Viewer Card */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card 
                  sx={{ 
                    position: 'relative',
                    bgcolor: viewerPreference === 'client' ? 'action.selected' : 'background.paper',
                    border: viewerPreference === 'client' ? 2 : 1,
                    borderColor: viewerPreference === 'client' ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => handleViewerPreferenceChange('client')}
                    disabled={viewerLoading}
                    sx={{ height: 160 }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <DesktopWindowsOutlinedIcon 
                        sx={{ 
                          fontSize: 56, 
                          color: 'orange',
                          mb: 1.5
                        }} 
                      />
                      <Typography variant="h6" component="div">
                        {t('profile.viewer.clientViewer')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('profile.viewer.desktopApplication')}
                      </Typography>
                      {viewerPreference === 'client' && (
                        <CheckIcon 
                          sx={{ 
                            position: 'absolute', 
                            top: 12, 
                            right: 12, 
                            color: 'primary.main' 
                          }} 
                        />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>

            {viewerLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </TabPanel>

          {/* Chat Settings Tab */}
          <TabPanel value={activeTab} index={3}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon color="primary" />
              {t('profile.chat.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {t('profile.chat.description')}
            </Typography>

            <Card sx={{ maxWidth: 500, p: 2 }}>
              <CardContent>
                {/* Mute Chat Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={chatSettings.muted}
                      onChange={(e) => {
                        const newSettings = { ...chatSettings, muted: e.target.checked };
                        handleChatSettingsChange(newSettings);
                      }}
                      disabled={chatLoading}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {chatSettings.muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                      <Box>
                        <Typography variant="body1">{t('profile.chat.muteChat')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('profile.chat.muteDescription')}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}
                />

                {/* Notification Sound Selector */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('profile.chat.notificationSound')}</InputLabel>
                  <Select
                    value={chatSettings.notificationSound}
                    label={t('profile.chat.notificationSound')}
                    onChange={(e) => {
                      const newSettings = { ...chatSettings, notificationSound: e.target.value };
                      handleChatSettingsChange(newSettings);
                    }}
                    disabled={chatLoading || chatSettings.muted}
                  >
                    <MenuItem value="3Dots">3Dots</MenuItem>
                    <MenuItem value="Bright">Bright</MenuItem>
                    <MenuItem value="HappyPop">HappyPop</MenuItem>
                    <MenuItem value="LevelUp">LevelUp</MenuItem>
                    <MenuItem value="Merimba">Merimba</MenuItem>
                    <MenuItem value="Xylo">Xylo</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                  {t('profile.chat.notificationSoundDescription')}
                </Typography>

                {/* Test Sound Button */}
                <Button
                  variant="outlined"
                  onClick={handleTestSound}
                  disabled={chatLoading || chatSettings.muted}
                  startIcon={<VolumeUpIcon />}
                  className="button"
                >
                  {t('profile.chat.testSound')}
                </Button>

                {chatLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress />
                  </Box>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* AI Settings Tab */}
          <TabPanel value={activeTab} index={4}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon sx={{ color: 'var(--rd-primary)' }} />
              {t('profile.aiSettings.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('profile.aiSettings.description')}
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('profile.aiSettings.summarySection')}
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenAiPromptDialog()}
                    sx={{ borderColor: 'var(--rd-primary)', color: 'var(--rd-primary)', '&:hover': { borderColor: 'var(--rd-primary-hover)', backgroundColor: 'color-mix(in srgb, var(--rd-primary) 4%, transparent)' } }}
                  >
                    {t('profile.aiSettings.addPrompt')}
                  </Button>
                </Box>

                {aiPrompts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    {t('profile.aiSettings.noPrompts')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {aiPrompts.map((prompt) => (
                      <Paper
                        key={prompt.id}
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2, borderColor: prompt.is_default ? 'var(--rd-primary)' : 'divider', backgroundColor: prompt.is_default ? 'color-mix(in srgb, var(--rd-primary) 3%, transparent)' : 'transparent' }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1, mr: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {prompt.name}
                              </Typography>
                              {prompt.is_default && (
                                <Chip label={t('profile.aiSettings.isDefault')} size="small" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: 'var(--rd-primary)', color: 'var(--rd-primary-contrast)' }} />
                              )}
                              {prompt.show_in_search && (
                                <Chip label={t('profile.aiSettings.showInSearch')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', borderColor: 'var(--rd-primary)', color: 'var(--rd-primary)' }} />
                              )}
                              {prompt.scope === 'current_exam' && (
                                <Chip label={t('profile.aiSettings.scopeLabel')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', borderColor: 'var(--rd-info)', color: 'var(--rd-info)' }} />
                              )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                              {prompt.prompt}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <IconButton size="small" onClick={() => handleToggleDefaultPrompt(prompt.id)} title={t('profile.aiSettings.setDefault')} sx={{ color: prompt.is_default ? 'var(--rd-primary)' : 'text.secondary' }}>
                              {prompt.is_default ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenAiPromptDialog(prompt)} sx={{ color: 'primary.main' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteAiPrompt(prompt)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* Signature Tab */}
          <TabPanel value={activeTab} index={5}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DriveFileRenameOutlineOutlinedIcon color="primary" />
              {t('profile.signature.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {t('profile.signature.help')}
            </Typography>
            
            <Card sx={{ maxWidth: 400, p: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      width: 200,
                      height: 100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: (theme) => alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    {signaturePreview ? (
                      <img
                        src={signaturePreview}
                        alt={t('profile.signature.previewAlt')}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('profile.signature.noSignature')}
                      </Typography>
                    )}
                  </Box>
                  
                  <Button
                    variant="contained"
                    component="label"
                    className="button"
                    startIcon={<DriveFileRenameOutlineOutlinedIcon />}
                  >
                    {t('profile.signature.uploadButton')}
                    <input
                      type="file"
                      accept="image/png"
                      hidden
                      onChange={handleSignatureChange}
                    />
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </TabPanel>

          {/* Snippets Tab */}
          <TabPanel value={activeTab} index={6}>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SnippetsIcon color="primary" />
              {t('profile.snippets.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {t('profile.snippets.description')}
            </Typography>

            {snippetsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={4}>
                {/* Admin/Group Snippets */}
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Card sx={{ p: 2, height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SettingsIcon color="primary" />
                        {t('profile.snippets.availableSnippets')}
                      </Typography>
                      <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                        {adminSnippets.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                            {t('profile.snippets.noAvailableSnippets')}
                          </Typography>
                        ) : (
                          adminSnippets.map((snippet) => (
                            <Paper 
                              key={snippet.id} 
                              elevation={0}
                              sx={{ 
                                mb: 1.5, 
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                '&:hover': {
                                  bgcolor: 'action.hover'
                                }
                              }}
                            >
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 'bold' }}>
                                @{snippet.label}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 0.5 }}>
                                {snippet.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mt: 0.5
                              }}>
                                {snippet.content}
                              </Typography>
                              <Chip 
                                size="small" 
                                label={t('profile.snippets.usedTimes', { count: snippet.used_x_times || 0 })}
                                sx={{ mt: 1, height: 22, fontSize: '0.7rem' }}
                              />
                            </Paper>
                          ))
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* User's Own Snippets */}
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Card sx={{ p: 2, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon color="primary" />
                          {t('profile.snippets.mySnippets')}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddSnippet}
                          className="button"
                        >
                          {t('profile.snippets.addSnippet')}
                        </Button>
                      </Box>
                      <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                        {userSnippets.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                            {t('profile.snippets.noOwnSnippets')}
                          </Typography>
                        ) : (
                          userSnippets.map((snippet) => (
                            <Paper 
                              key={snippet.id} 
                              elevation={0}
                              sx={{ 
                                mb: 1.5, 
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                '&:hover': {
                                  bgcolor: 'action.hover'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 'bold' }}>
                                    @{snippet.label}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 0.5 }}>
                                    {snippet.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mt: 0.5
                                  }}>
                                    {snippet.content}
                                  </Typography>
                                  <Chip 
                                    size="small" 
                                    label={t('profile.snippets.usedTimes', { count: snippet.used_x_times || 0 })}
                                    sx={{ mt: 1, height: 22, fontSize: '0.7rem' }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleEditSnippet(snippet)}
                                    color="primary"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleDeleteSnippet(snippet)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          ))
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* Snippet Add/Edit Dialog */}
      <Dialog open={snippetDialogOpen} onClose={() => setSnippetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSnippet 
            ? t('profile.snippets.editSnippet')
            : t('profile.snippets.addSnippet')
          }
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('profile.snippets.label')}
                value={snippetFormData.label}
                onChange={(e) => setSnippetFormData({ ...snippetFormData, label: e.target.value.toUpperCase() })}
                placeholder="TEXT-1234"
                helperText={t('profile.snippets.labelHelp')}
                inputProps={{ pattern: '[A-ZÄÖÜ0-9-]+' }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('profile.snippets.name')}
                value={snippetFormData.name}
                onChange={(e) => setSnippetFormData({ ...snippetFormData, name: e.target.value })}
                placeholder={t('profile.snippets.namePlaceholder')}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('profile.snippets.content')}
                value={snippetFormData.content}
                onChange={(e) => setSnippetFormData({ ...snippetFormData, content: e.target.value })}
                placeholder={t('profile.snippets.contentPlaceholder')}
              />
            </Grid>
            <Grid size={12}>
              <Alert severity="info">
                {t('profile.snippets.userSnippetInfo')}
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnippetDialogOpen(false)}>
            {t('profile.snippets.cancel')}
          </Button>
          <Button 
            onClick={handleSaveSnippet} 
            variant="contained" 
            className="button"
          >
            {editingSnippet 
              ? t('profile.snippets.updateSnippet')
              : t('profile.snippets.createSnippet')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteSnippetDialogOpen} onClose={() => setDeleteSnippetDialogOpen(false)}>
        <DialogTitle>{t('profile.snippets.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('profile.snippets.deleteConfirm.message', { 
              name: snippetToDelete?.name,
              label: snippetToDelete?.label 
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSnippetDialogOpen(false)}>
            {t('profile.snippets.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteSnippet} 
            color="error" 
            variant="contained"
          >
            {t('profile.snippets.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Prompt Add/Edit Dialog */}
      <Dialog open={aiPromptDialogOpen} onClose={() => setAiPromptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAiPrompt ? t('profile.aiSettings.editPrompt') : t('profile.aiSettings.addPrompt')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('profile.aiSettings.promptName')}
            value={aiPromptFormData.name}
            onChange={(e) => setAiPromptFormData({ ...aiPromptFormData, name: e.target.value })}
            placeholder={t('profile.aiSettings.promptNamePlaceholder')}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={14}
            label={t('profile.aiSettings.promptText')}
            value={aiPromptFormData.prompt}
            onChange={(e) => setAiPromptFormData({ ...aiPromptFormData, prompt: e.target.value })}
            placeholder={t('profile.aiSettings.promptTextPlaceholder')}
          />
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={aiPromptFormData.show_in_search}
                onChange={(e) => setAiPromptFormData({ ...aiPromptFormData, show_in_search: e.target.checked })}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--rd-primary)' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--rd-primary)' } }}
              />
            }
            label={t('profile.aiSettings.showInSearch')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
            {t('profile.aiSettings.showInSearchDescription')}
          </Typography>
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Switch
                checked={aiPromptFormData.scope === 'current_exam'}
                onChange={(e) => setAiPromptFormData({ ...aiPromptFormData, scope: e.target.checked ? 'current_exam' : 'full_history' })}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--rd-info)' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--rd-info)' } }}
              />
            }
            label={t('profile.aiSettings.scopeCurrentExam')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
            {t('profile.aiSettings.scopeCurrentExamDescription')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiPromptDialogOpen(false)}>
            {t('profile.aiSettings.cancel')}
          </Button>
          <Button
            onClick={handleSaveAiPrompt}
            variant="contained"
            disabled={!aiPromptFormData.name.trim() || !aiPromptFormData.prompt.trim()}
            sx={{ backgroundColor: 'var(--rd-primary)', '&:hover': { backgroundColor: 'var(--rd-primary-hover)' } }}
          >
            {t('profile.aiSettings.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Prompt Delete Confirmation Dialog */}
      <Dialog open={deleteAiPromptDialogOpen} onClose={() => setDeleteAiPromptDialogOpen(false)}>
        <DialogTitle>{t('profile.aiSettings.deletePrompt')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('profile.aiSettings.deleteConfirm', { name: aiPromptToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAiPromptDialogOpen(false)}>
            {t('profile.aiSettings.cancel')}
          </Button>
          <Button onClick={confirmDeleteAiPrompt} color="error" variant="contained">
            {t('profile.aiSettings.deletePrompt')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
